import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Stack,
  TextField,
} from "@mui/material";
import { AccountPageLayout } from "components/common/AccountPageLayout";
import dayjs, { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { Serialiser } from "hooks/serialisers/types";
import { useTenant } from "hooks/settings";
import { useMaterials } from "hooks/useMaterials";
import { useQueryParamState } from "hooks/useQueryParamState";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ConsumptionProjectionMaterialItemRead,
  useCreateConsumptionProjectionMutation,
  useGetLatestConsumptionProjectionQuery,
} from "src/store/api/generatedApi";
import { ProjectionsGrid } from "./ProjectionsGrid";
import { ProjectionsToolbar } from "./ProjectionsToolbar";
import type { PasteUpdate } from "./types";
import { withUpdatedRowFractions } from "./projectionUtils";
const dayjsSerailiser: Serialiser<Dayjs, never> = {
  format: (value) => value.format("YYYY-MM-DD"),
  parse: (text) => ({ valid: true, value: dayjs(text) }),
};

export const ConsumptionProjectionsPage = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();

  const [selectedMonth, setSelectedMonth] = useQueryParamState<Dayjs, never>(
    "month",
    dayjsSerailiser,
    dayjs()
  );

  const [createConsumptionProjection] =
    useCreateConsumptionProjectionMutation();
  const { data: latestConsumptionProjection, error } =
    useGetLatestConsumptionProjectionQuery({
      tenantName,
      month: selectedMonth.format("YYYY-MM-DD"),
    });
  const currentMonthProjection = latestConsumptionProjection?.items;
  const [rows, setRows] = useState<ConsumptionProjectionMaterialItemRead[]>([]);

  useEffect(() => {
    if (currentMonthProjection) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- syncs editable rows from API; user modifies locally
      setRows(currentMonthProjection);
    } else {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- clears editable rows when no API data
      setRows([]);
    }
  }, [currentMonthProjection]);

  const { materials, isLoading: materialsLoading } = useMaterials();
  const availableMaterialNames = useMemo(() => {
    const all = materials?.map((m) => m.name) ?? [];
    const existing = new Set(rows.map((r) => r.material_name));
    return all.filter((name) => !existing.has(name));
  }, [materials, rows]);
  const [newMaterialName, setNewMaterialName] = useState<string | null>(null);
  const [newQuantity, setNewQuantity] = useState(0);

  const hasRows = rows.length > 0;
  const hasChanges = useMemo(() => {
    const base = currentMonthProjection ?? [];
    if (rows.length !== base.length) return true;
    const baseMap = new Map(
      base.map((item) => [item.material_name, item.quantity] as const)
    );
    for (const row of rows) {
      if (baseMap.get(row.material_name) !== row.quantity) return true;
    }
    return false;
  }, [rows, currentMonthProjection]);

  const handleReset = useCallback(() => {
    setRows(currentMonthProjection ?? []);
  }, [currentMonthProjection]);

  const handleSave = useCallback(async () => {
    await createConsumptionProjection({
      tenantName,
      consumptionProjectionCreate: {
        month: selectedMonth.format("YYYY-MM-DD"),
        items: rows.map((r) => ({
          material_name: r.material_name,
          quantity: r.quantity,
        })),
      },
    });
  }, [createConsumptionProjection, tenantName, selectedMonth, rows]);

  const handleAddRow = useCallback(() => {
    if (!newMaterialName) return;
    setRows((prev) => {
      if (prev.some((r) => r.material_name === newMaterialName)) {
        return prev;
      }
      const merged = [
        ...prev,
        {
          material_name: newMaterialName,
          quantity: newQuantity || 0,
          fraction: 0,
        },
      ];
      return withUpdatedRowFractions(merged);
    });
    setNewMaterialName(null);
    setNewQuantity(0);
  }, [newMaterialName, newQuantity]);

  const handleApplyPastedUpdates = useCallback((updates: PasteUpdate[]) => {
    setRows((prev) => {
      const existingNames = new Set(prev.map((r) => r.material_name));
      const updatedExisting = prev.map((row) => {
        const match = updates.find(
          (u) => u.material_name === row.material_name
        );
        return match ? { ...row, quantity: match.quantity } : row;
      });

      const additions: ConsumptionProjectionMaterialItemRead[] = updates
        .filter((u) => !existingNames.has(u.material_name))
        .map((u) => ({
          material_name: u.material_name,
          quantity: u.quantity,
          fraction: 0,
        }));

      const merged = [...updatedExisting, ...additions];
      return withUpdatedRowFractions(merged);
    });
  }, []);

  const onProcessRowUpdate = useCallback(
    (newRow: ConsumptionProjectionMaterialItemRead) =>
      new Promise<ConsumptionProjectionMaterialItemRead>((resolve) => {
        setRows((prev) => {
          const newRows = prev.map((row) =>
            row.material_name === newRow.material_name ? newRow : row
          );
          const updatedRows = withUpdatedRowFractions(newRows);
          resolve(
            updatedRows.find(
              (row) => row.material_name === newRow.material_name
            ) ?? newRow
          );
          return updatedRows;
        });
      }),
    []
  );

  return (
    <AccountPageLayout title={t("projections")}>
      <Box
        sx={{
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: 2,
        }}
      >
        <ProjectionsToolbar
          selectedMonth={selectedMonth}
          setSelectedMonth={(d) => {
            setRows([]);
            setSelectedMonth(d);
          }}
          hasRows={hasRows}
          hasChanges={hasChanges}
          onSave={handleSave}
          onReset={handleReset}
          onApplyPastedUpdates={handleApplyPastedUpdates}
        >
          <Stack direction="row" spacing={1}>
            <Autocomplete
              size="small"
              sx={{ minWidth: 240 }}
              options={availableMaterialNames}
              loading={materialsLoading}
              value={newMaterialName}
              onChange={(_e, value) => {
                setNewMaterialName(value);
              }}
              renderInput={(params) => (
                <TextField {...params} label={t("material")} />
              )}
            />
            <TextField
              size="small"
              type="number"
              label={t("projectedConsumption")}
              value={Number.isFinite(newQuantity) ? newQuantity : ""}
              onChange={(e) => {
                setNewQuantity(
                  Number.isNaN(parseFloat(e.target.value))
                    ? 0
                    : parseFloat(e.target.value)
                );
              }}
              sx={{ width: 160 }}
              slotProps={{ htmlInput: { step: "any" } }}
            />
            <Button
              variant="outlined"
              onClick={handleAddRow}
              disabled={!newMaterialName}
            >
              {t("add")}
            </Button>
          </Stack>
        </ProjectionsToolbar>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("errorLoadingData")}
          </Alert>
        ) : null}
        <ProjectionsGrid rows={rows} onProcessRowUpdate={onProcessRowUpdate} />
      </Box>
    </AccountPageLayout>
  );
};
